import colors from '@toolshed/styleguide/styles/colors';
import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';

import { IconWarning } from '@toolshed/styleguide/components/AppIcon/AppIcons';

import { Description, Suggestion, Title, Wrapper } from './styles';

const locationNotFound = {
  title: 'Sorry, we are unable to find that location',
  description:
    'Please check:\r\n- The location is spelled correctly\r\n- The postcode is valid',
};

const categoryNotFound = {
  title: 'Category not found',
  suggestion: 'You have not completed a search.',
  description: 'Unable to find the category.',
};

export const typeTranslationMap = {
  'faraday/resource_not_found': locationNotFound,
  '/trade-card/location-error': locationNotFound,
  'api/resources/search/location_not_found_error': locationNotFound,
  'api/resources/exceptions/location_not_found_error': locationNotFound,
  'api/resources/search/category_not_found_error': categoryNotFound,
  resource_not_found: locationNotFound,
  location_not_found_error: locationNotFound,
  category_not_found_error: categoryNotFound,
  not_found: {
    title: 'No companies found',
    suggestion: 'Please try broadening your search.',
  }, // TODO: consider asking wag to return not found when no companies match search
  _network: {
    title: 'Sorry! We are unable to complete your request at this time.',
    suggestion: 'Please try again later.',
  }, // TODO: once we figure it out surface a nicer error
  unknown: {
    title: 'Sorry! We are unable to complete your request at this time.',
    suggestion: 'Please try again later.',
  },
};

export const SearchError = ({ onTrackEvent, error }) => {
  // if backend error, try and get its type.
  // if not an error, default to not found, since this will only surface if showError is true.
  const { data, status } = error || {};
  const type = error ? (error.data || {}).type : 'not_found';

  const hasTranslation = useMemo(
    () => Object.prototype.hasOwnProperty.call(typeTranslationMap, type),
    [type],
  );
  const key = hasTranslation ? type : 'unknown';
  const { title, suggestion, description } = hasTranslation
    ? typeTranslationMap[type]
    : typeTranslationMap.unknown;

  useEffect(() => {
    onTrackEvent({
      key: 'error.message',
      data: {
        response_code: type,
        message: key,
        error_data: data,
        error_status: status,
      },
    });
  }, [type]);

  return (
    <Wrapper>
      <IconWarning size="xlarge" color={colors.red} />
      <Title>{title}</Title>
      {suggestion && <Suggestion>{suggestion}</Suggestion>}
      {description && <Description>{description}</Description>}
    </Wrapper>
  );
};

SearchError.propTypes = {
  error: PropTypes.object,
  onTrackEvent: PropTypes.func.isRequired,
};

export default SearchError;
